import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useAuth } from '../App';
import axios from 'axios';
import { API_URL } from '../utils/config';
import {
  Key,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle
} from 'lucide-react';


const PlatformCredentials = () => {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [showPassword, setShowPassword] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form state
  const [formData, setFormData] = useState({
    platform: 'booking',
    username: '',
    password: ''
  });

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API_URL}/api/credentials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCredentials(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Errore nel caricamento credenziali:', error);
      showMessage('error', 'Errore nel caricamento delle credenziali');
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAddCredential = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      // Backend expects query parameters, not JSON body
      const params = new URLSearchParams({
        platform: formData.platform,
        username: formData.username,
        password: formData.password,
        requires_2fa: 'false'
      });
      
      await axios.post(`${API_URL}/api/credentials?${params.toString()}`, null, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      showMessage('success', 'Credenziali aggiunte con successo');
      setShowAddDialog(false);
      setFormData({ platform: 'booking', username: '', password: '' });
      fetchCredentials();
    } catch (error) {
      console.error('Errore nell\'aggiunta credenziali:', error);
      showMessage('error', 'Errore nell\'aggiunta delle credenziali');
    }
  };

  const handleUpdateCredential = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      
      // Backend expects query parameters, not JSON body
      const params = new URLSearchParams({
        platform: formData.platform,
        username: formData.username,
        requires_2fa: 'false'
      });
      
      // Se la password è vuota, non la inviamo (mantiene quella esistente)
      if (formData.password) {
        params.append('password', formData.password);
      }

      await axios.post(`${API_URL}/api/credentials?${params.toString()}`, null, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      showMessage('success', 'Credenziali aggiornate con successo');
      setShowEditDialog(false);
      setSelectedCredential(null);
      setFormData({ platform: 'booking', username: '', password: '' });
      fetchCredentials();
    } catch (error) {
      console.error('Errore nell\'aggiornamento credenziali:', error);
      showMessage('error', 'Errore nell\'aggiornamento delle credenziali');
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (!window.confirm('Sei sicuro di voler eliminare queste credenziali?')) {
      return;
    }

    try {
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API_URL}/api/credentials/${credentialId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      showMessage('success', 'Credenziali eliminate con successo');
      fetchCredentials();
    } catch (error) {
      console.error('Errore nell\'eliminazione credenziali:', error);
      showMessage('error', 'Errore nell\'eliminazione delle credenziali');
    }
  };

  const openEditDialog = (credential) => {
    setSelectedCredential(credential);
    setFormData({
      platform: credential.platform,
      username: credential.username,
      password: '' // Non mostriamo la password per sicurezza
    });
    setShowEditDialog(true);
  };

  const togglePasswordVisibility = (credentialId) => {
    setShowPassword(prev => ({
      ...prev,
      [credentialId]: !prev[credentialId]
    }));
  };

  const getPlatformIcon = (platform) => {
    if (platform === 'booking') return '🏨';
    if (platform === 'airbnb') return '🏠';
    return '🔑';
  };

  const getPlatformName = (platform) => {
    if (platform === 'booking') return 'Booking.com';
    if (platform === 'airbnb') return 'Airbnb';
    return platform;
  };

  if (loading) {
    return (
      <Layout title="Credenziali Piattaforme">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
            <p className="text-slate-600">Caricamento credenziali...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Credenziali Piattaforme">
      <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Key className="h-8 w-8 text-blue-900" />
            Credenziali Piattaforme
          </h1>
          <p className="text-slate-600 mt-2">
            Gestisci le credenziali di accesso per Booking.com e Airbnb
          </p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Aggiungi Credenziali
        </Button>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          {message.text}
        </div>
      )}

      {/* Credentials Table */}
      <Card className="overflow-hidden shadow-lg">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                  Piattaforma
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                  Username
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                  Password
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                  Data Creazione
                </th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {credentials.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                    <Key className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">Nessuna credenziale configurata</p>
                    <p className="text-sm mt-2">Aggiungi le tue credenziali per iniziare</p>
                  </td>
                </tr>
              ) : (
                credentials.map((credential) => (
                  <tr key={credential.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{getPlatformIcon(credential.platform)}</span>
                        <span className="font-medium text-slate-900">
                          {getPlatformName(credential.platform)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-700">
                      {credential.username}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono text-slate-700">
                          {showPassword[credential.id] ? credential.password : '••••••••'}
                        </code>
                        <button
                          onClick={() => togglePasswordVisibility(credential.id)}
                          className="text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showPassword[credential.id] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600 text-sm">
                      {new Date(credential.created_at).toLocaleDateString('it-IT', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(credential)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCredential(credential.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Credential Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Aggiungi Nuove Credenziali</h2>
            <form onSubmit={handleAddCredential} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Piattaforma
                </label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="booking">Booking.com</option>
                  <option value="airbnb">Airbnb</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Username / Email
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="username@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="••••••••"
                  required
                />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  ℹ️ <strong>Nota:</strong> Per l'autenticazione 2FA, il codice OTP dovrà essere inserito manualmente quando richiesto durante lo scraping.
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowAddDialog(false);
                    setFormData({ platform: 'booking', username: '', password: '' });
                  }}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
                >
                  Salva
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Edit Credential Dialog */}
      {showEditDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 space-y-4">
            <h2 className="text-xl font-bold text-slate-900">Modifica Credenziali</h2>
            <form onSubmit={handleUpdateCredential} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Piattaforma
                </label>
                <select
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="booking">Booking.com</option>
                  <option value="airbnb">Airbnb</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Username / Email
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="username@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Lascia vuoto per mantenere la password esistente"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Lascia vuoto per non modificare la password
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowEditDialog(false);
                    setSelectedCredential(null);
                    setFormData({ platform: 'booking', username: '', password: '' });
                  }}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
                >
                  Aggiorna
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default PlatformCredentials;
